import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivityQueueDialogComponent } from '../activityQueueDialog/activityQueueDialog.component';
import { SseService } from '../../services/sse.service';
import { switchMap } from 'rxjs';
import { Job, JobsService, AssetMetadataService } from '@dal/core';
import { MatSelectChange } from '@angular/material/select';
import {
  ACTIVITY_QUEUE_CSV_FIELDS,
  ACTIVITY_QUEUE_CSV_HEADERS,
} from '../../constants/activityQueuePage.constants';
import { ACTIVITY_QUEUE_COLUMNS } from './activityQueue.constants';
import { ColumnConfig } from '@dal/shared-ui';

@Component({
  selector: 'dal-activity-queue-page',
  templateUrl: './activityQueuePage.component.html',
  styleUrl: './activityQueuePage.component.scss',
})
export class ActivityQueuePageComponent implements OnInit {
  jobList: Job[] = [];
  type: any;
  status: any;
  searchkey: string = '';
  title: string = 'Activities Queue';
  currentPage: number = 1;
  dialogLink = true;
  statusList = [
    { id: 1, value: 'Uploading' },
    { id: 2, value: 'Processing' },
    { id: 3, value: 'Failed' },
    { id: 4, value: 'Success' },
  ];
  typeList = [
    { id: 1, value: 'Import' },
    { id: 2, value: 'Bulk Edit' },
    { id: 3, value: 'Export' },
    { id: 4, value: 'Retranscode' },
  ];
  displayedColumns: ColumnConfig[] = ACTIVITY_QUEUE_COLUMNS;
  itemsPerPage: number = 10;

  constructor(
    public dialog: MatDialog,
    public _jobs: JobsService,
    private _sse: SseService,
    private _assetMetadataService: AssetMetadataService
  ) {}

  ngOnInit(): void {
    this._jobs
      .getJobs()
      .pipe(
        switchMap((response: Job[]) => {
          this.updateTableData(response);
          // Return the SSE stream observable
          return this._sse.connectToJobsStream();
        })
      )
      .subscribe({
        next: (data: Job) => {
          console.log('Received message:', data);
          this.updateTableData([data]);
        },
        error: (error) => {
          console.error('Error:', error);
        },
        complete: () => {
          console.log('Stream completed.');
        },
      });
  }

  search(event: any) {
    console.log(event.target.value);
    this.currentPage = 1;
    this.searchkey = event.target.value;
  }
  statusChanged(event: MatSelectChange) {
    console.log(event);
    this.currentPage = 1;
    this.status = event.value;
  }
  typeChanged(event: MatSelectChange) {
    console.log(event);
    this.currentPage = 1;
    this.type = event.value;
  }
  pageChanged(event: any) {
    console.log(event);
    this.currentPage = event.currentPage;
  }
  reset() {
    console.log('reset');
    this.currentPage = 1;
    this.status = '';
    this.type = '';
    this.searchkey = '';
  }
  // Function to update the jobList with new data
  updateTableData(jobUpdate: Job[]): void {
    if (this.jobList.length === 0) {
      this.jobList = jobUpdate;
    } else {
      for (let i = 0; i < jobUpdate.length; i++) {
        const job = jobUpdate[i];
        // Check if the job already exists in the jobList
        const existingJobIndex = this.jobList.findIndex(
          (existingJob: any) => existingJob.job_id === job.job_id
        );
        if (existingJobIndex !== -1) {
          // Update the existing job
          this.jobList[existingJobIndex] = job;
        } else {
          // Add the new job to the jobList
          this.jobList.unshift(job);
        }
      }
    }
    // Update the reference to trigger change detection
    this.jobList = [...this.jobList];
  }

  // Apply filters to the jobList and paginate the results
  applyFilters(list: Job[]): Job[] {
    return list.filter((job: Job) => {
      const matchesType = this.type ? job.job_type_id === this.type.id : true;
      const matchesStatus = this.status
        ? job.status_id === this.status.id
        : true;
      const matchesSearchKey = this.searchkey
        ? job.created_by.toLowerCase().includes(this.searchkey.toLowerCase())
        : true;
      return matchesType && matchesStatus && matchesSearchKey;
    });
  }

  paginate(list: Job[]): Job[] {
    return list.slice(
      (this.currentPage - 1) * this.itemsPerPage,
      this.currentPage * this.itemsPerPage
    );
  }

  getData(list: Job[]): Job[] {
    const filteredList = this.applyFilters(list);
    return this.paginate(filteredList);
  }

  openDialog(job: Job) {
    this.dialog.open(ActivityQueueDialogComponent, {
      data: job,
    });

    //handle dialog close
  }

  downloadReport(assetIDs: number[]) {
    this._assetMetadataService
      .getMetadataBulk(assetIDs)
      .subscribe((response: any) => {
        const csvContent = this.generateCSVFromAssets(response);
        this.downloadCSV(csvContent);
      });
  }

  generateCSVFromAssets(response: any[]): string {
    const headers = ACTIVITY_QUEUE_CSV_HEADERS;
    const assetFields = ACTIVITY_QUEUE_CSV_FIELDS;

    const escapeCSV = (value: string) =>
      `"${(value ?? '').replace(/"/g, '""')}"`;

    const rows = response.map((entry) => {
      return assetFields
        .map((field) => {
          if (field.type === 'asset') {
            return escapeCSV(entry.asset?.[field.key] ?? '');
          } else {
            const md = entry.metadata?.find((m: any) => m.key === field.key);
            return escapeCSV(md?.values?.join('; ') ?? '');
          }
        })
        .join(',');
    });

    return [headers.join(','), ...rows].join('\n');
  }

  downloadCSV(csvContent: string) {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.setAttribute('download', 'asset_report.csv');
    anchor.click();
    URL.revokeObjectURL(url);
  }
}
